import { Fragment, useMemo, useState , useEffect} from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Badge, Button, EditArticleButton } from '@/components/atoms';
import { useChartTheme } from '@/components/Charts/theme';
import { budgetsApi, destinationsApi } from '@/services/config';
import { metricsApi, type RundownRow, type RankingAutor, type AutorArtigo } from '@/services/metrics';
import { optimizationApi } from '@/services/optimization';
import { Otimizacao } from '@/pages/Otimizacao';
import { formatarNumero } from '@/i18n/formato';

import styles from './Analytics.module.css';

const TABS = [
  { key: 'agora', label: 'Agora', desc: 'Tempo real — quem está lendo agora e o que bomba neste minuto.' },
  { key: 'operacional', label: 'Operacional', desc: 'O dia a dia — rundown das matérias com alertas e ação.' },
  { key: 'estrategico', label: 'Estratégico', desc: 'A visão macro — KPIs do site, evolução e busca consolidada.' },
  { key: 'seo', label: 'SEO', desc: 'Sinais de SEO priorizados + correção em 1 clique.' },
  { key: 'autores', label: 'Autores', desc: 'Quem produziu trouxe quanta audiência — ranking + CTR, pageview e tempo de leitura por artigo.' },
  { key: 'custo', label: 'Custo', desc: 'Quanto custou produzir — por dia, por fonte e por redator (metering em tempo real).' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

const fmt = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(Math.round(n));
};
const secs = (s: number): string => {
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return m ? `${m}m${r.toString().padStart(2, '0')}s` : `${r}s`;
};
export function Analytics() {
  const { t } = useTranslation(['common', 'nav', 'board', 'articles', 'analytics', 'feeddrive', 'config', 'strategist']);
  const { chartTheme, chartTooltipStyle } = useChartTheme();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as TabKey) || 'agora';
  const setTab = (t: TabKey) => setParams({ tab: t });
  // B-34 F0: sem default global — a marca vem dos destinos DO TENANT (backend
  // escopa; site vazio = 1ª marca do cliente, nunca a de outro).
  const [site, setSite] = useState('');
  const sitesQ = useQuery({
    queryKey: ['wp-sites', 'active'],
    queryFn: () => destinationsApi.list(),
    staleTime: 300_000,
  });
  useEffect(() => {
    if (!site && sitesQ.data?.length) setSite(sitesQ.data[0].slug);
  }, [site, sitesQ.data]);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['metrics', 'overview', site],
    queryFn: () => metricsApi.overview(site),
    staleTime: 5 * 60_000,
    retry: false,
    enabled: tab === 'estrategico',
  });
  const seriesQ = useQuery({
    queryKey: ['metrics', 'timeseries', site],
    queryFn: () => metricsApi.timeseries(site),
    staleTime: 5 * 60_000,
    retry: false,
    enabled: tab === 'estrategico',
  });
  const series = (seriesQ.data ?? []).map((d) => ({
    ...d,
    dia: d.date.slice(5), // MM-DD
    ctr_pct: (d.ctr ?? 0) * 100, // CTR em % para o eixo
  }));
  const rt = useQuery({
    queryKey: ['metrics', 'realtime', site],
    queryFn: () => metricsApi.realtime(site),
    refetchInterval: tab === 'agora' ? 30_000 : false,
    retry: false,
    enabled: tab === 'agora',
  });

  const buckets = data?.position_buckets ?? [];
  const bucketColor = [chartTheme.ok, chartTheme.brand, chartTheme.warn, chartTheme.crit];

  // Nomes que aparecem mais de uma vez na lista de sites deste acesso.
  const nomeAmbiguo = useMemo(() => {
    const vistos = new Set<string>();
    const repetidos = new Set<string>();
    for (const s of sitesQ.data ?? []) {
      if (vistos.has(s.name)) repetidos.add(s.name);
      vistos.add(s.name);
    }
    return repetidos;
  }, [sitesQ.data]);

  return (
    <section className={styles.root} aria-label={t('analytics:title', 'Métricas')}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Estratégia</h1>
          <p className={styles.subtitle}>{TABS.find((t) => t.key === tab)?.desc}</p>
        </div>
        <select className={styles.select} value={site} onChange={(e) => setSite(e.target.value)}>
          {(sitesQ.data ?? []).map((s) => (
            // Duas marcas com o MESMO nome viram duas linhas identicas e nao ha
            // como saber qual e qual (medido: "Site A" aparecia duas vezes, com
            // slugs diferentes). So desambigua quando precisa — poluir todas as
            // linhas com o slug seria pior para quem tem nomes unicos.
            <option key={s.slug} value={s.slug}>
              {nomeAmbiguo.has(s.name) ? `${s.name} · ${s.slug}` : s.name}
            </option>
          ))}
        </select>
      </header>

      <nav className={styles.tabs}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'agora' && (rt.data?.configured ? (
        <div className={styles.rt}>
          <div className={styles.rtNow}>
            <div className={styles.rtHead}>
              <span className={styles.rtDot} />
              <span className={styles.rtLabel}>Agora · tempo real (GA4)</span>
            </div>
            <span className={styles.rtBig}>{formatarNumero(rt.data.active_users)}</span>
            <span className={styles.rtSub}>usuários lendo agora · últimos 30 min</span>
            <ResponsiveContainer width="100%" height={48}>
              <BarChart data={[...rt.data.by_minute].reverse()}>
                <Bar dataKey="active_users" fill={chartTheme.ok} radius={[2, 2, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className={styles.rtPages}>
            <span className={styles.rtPagesTitle}>Matérias sendo lidas agora</span>
            {/* A página de erro NÃO entra na lista (o backend separa): era
                robô batendo em /photo e afins, e "Página não encontrada"
                aparecia como a matéria mais lida do site. O contador abaixo
                mantém o problema visível sem ele se disfarçar de audiência. */}
            {(rt.data.broken_users ?? 0) > 0 && (
              <span className={styles.rtBroken} title="Acessos em URLs quebradas (404) neste momento — majoritariamente tráfego automatizado; ver relatório de correção de 404">
                {rt.data.broken_users} em páginas quebradas (fora da lista)
              </span>
            )}
            <ul className={styles.rtList}>
              {rt.data.top_pages.slice(0, 8).map((p, i) => (
                <li key={i} className={styles.rtItem}>
                  <span className={styles.rtCount}>{p.active_users}</span>
                  <span className={styles.rtName}>{p.name}</span>
                </li>
              ))}
              {rt.data.top_pages.length === 0 && <li className={styles.muted}>Ninguém ativo no momento.</li>}
            </ul>
          </div>
        </div>
      ) : (
        <p className={styles.empty}>
          Tempo real precisa do GA4 conectado neste site (Config → Desempenho).
        </p>
      ))}

      {tab === 'operacional' && <RundownSection site={site} />}

      {tab === 'estrategico' && (
        <>
          {isLoading && <p className={styles.muted}>Carregando…</p>}
          {(isError || (data && data.articles === 0)) && (
            <p className={styles.empty}>
              Sem dados ainda para <b>{site}</b>. Conecte o GA4/Search Console em
              {' '}<b>Config → Desempenho</b> e rode o sync.
            </p>
          )}

          {data && data.articles > 0 && (
            <>
              {/* KPIs */}
          <div className={styles.kpis}>
            <Kpi group="GA4" label="pageviews" value={fmt(data.pageviews)} big />
            <Kpi group="GA4" label="sessões" value={fmt(data.sessions)} />
            <Kpi group="GA4" label="engajamento" value={`${Math.round(data.avg_engagement_rate * 100)}%`} />
            <Kpi group="GA4" label="tempo médio" value={secs(data.avg_engagement_time)} />
            <Kpi group="Search Console" label="cliques" value={fmt(data.gsc_clicks)} big />
            <Kpi group="Search Console" label="impressões" value={fmt(data.gsc_impressions)} />
            <Kpi group="Search Console" label="CTR" value={`${(data.gsc_ctr * 100).toFixed(1)}%`} />
            <Kpi
              group="Search Console"
              label="posição média"
              value={data.gsc_avg_position != null ? data.gsc_avg_position.toFixed(1) : '—'}
            />
          </div>

          {/* Evolução (série diária) */}
          {series.length > 1 && (
            <div className={styles.cardWide}>
              <h2 className={styles.cardTitle}>Evolução — tráfego (GA4) × cliques (Search Console)</h2>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={series} margin={{ left: 8, right: 8, top: 8 }}>
                  <CartesianGrid stroke={chartTheme.grid} vertical={false} />
                  <XAxis dataKey="dia" stroke={chartTheme.axis} tick={{ fontSize: 10, fill: chartTheme.textMuted }} minTickGap={20} />
                  <YAxis yAxisId="l" stroke={chartTheme.axis} tick={{ fontSize: 10, fill: chartTheme.textMuted }} />
                  <YAxis yAxisId="r" orientation="right" stroke={chartTheme.axis} tick={{ fontSize: 10, fill: chartTheme.textMuted }} />
                  <Tooltip contentStyle={chartTooltipStyle} />
                  <Area yAxisId="l" type="monotone" dataKey="pageviews" name="pageviews" stroke={chartTheme.brand} fill={chartTheme.brandBg} strokeWidth={2} />
                  <Line yAxisId="r" type="monotone" dataKey="clicks" name="cliques" stroke={chartTheme.ok} strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {series.length > 1 && (
            <div className={styles.cardWide}>
              <h2 className={styles.cardTitle}>Search Console — exposição (impressões) × CTR</h2>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={series} margin={{ left: 8, right: 8, top: 8 }}>
                  <CartesianGrid stroke={chartTheme.grid} vertical={false} />
                  <XAxis dataKey="dia" stroke={chartTheme.axis} tick={{ fontSize: 10, fill: chartTheme.textMuted }} minTickGap={20} />
                  <YAxis yAxisId="l" stroke={chartTheme.axis} tick={{ fontSize: 10, fill: chartTheme.textMuted }} />
                  <YAxis yAxisId="r" orientation="right" stroke={chartTheme.axis} tick={{ fontSize: 10, fill: chartTheme.textMuted }} unit="%" />
                  <Tooltip contentStyle={chartTooltipStyle} formatter={(v: number, n: string) => (n === 'CTR' ? `${v.toFixed(2)}%` : fmt(v))} />
                  <Area yAxisId="l" type="monotone" dataKey="impressions" name="impressões" stroke={chartTheme.brand} fill={chartTheme.brandBg} strokeWidth={2} />
                  <Line yAxisId="r" type="monotone" dataKey="ctr_pct" name="CTR" stroke={chartTheme.warn} strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
              <p className={styles.hint}>Impressões (área) crescendo com CTR (linha) baixo ou estável = oportunidade de headline.</p>
            </div>
          )}

          {series.length > 1 && (
            <div className={styles.cardWide}>
              <h2 className={styles.cardTitle}>Search Console — posição média no Google (menor é melhor)</h2>
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={series} margin={{ left: 8, right: 8, top: 8 }}>
                  <CartesianGrid stroke={chartTheme.grid} vertical={false} />
                  <XAxis dataKey="dia" stroke={chartTheme.axis} tick={{ fontSize: 10, fill: chartTheme.textMuted }} minTickGap={20} />
                  <YAxis reversed stroke={chartTheme.axis} tick={{ fontSize: 10, fill: chartTheme.textMuted }} domain={['dataMin - 1', 'dataMax + 1']} allowDecimals={false} />
                  <Tooltip contentStyle={chartTooltipStyle} formatter={(v: number) => (v == null ? '—' : v.toFixed(1))} />
                  <Line type="monotone" dataKey="position" name="posição" stroke={chartTheme.ok} strokeWidth={2} dot={false} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className={styles.grid}>
            {/* Distribuição de posição no Google */}
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Posição no Google (nº de matérias)</h2>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={buckets} margin={{ left: 8, right: 16, top: 16 }}>
                  <XAxis dataKey="label" stroke={chartTheme.axis} tick={{ fontSize: 12, fill: chartTheme.text }} />
                  <YAxis allowDecimals={false} stroke={chartTheme.axis} tick={{ fontSize: 11, fill: chartTheme.textMuted }} />
                  <Tooltip contentStyle={chartTooltipStyle} cursor={{ fill: chartTheme.brandBg }} />
                  <Bar dataKey="count" name="matérias" radius={[3, 3, 0, 0]}>
                    {buckets.map((_, i) => (
                      <Cell key={i} fill={bucketColor[i] ?? chartTheme.brand} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className={styles.hint}>1–3 = topo · 4–10 = 1ª página · 11–20 = 2ª página · 21+ = fundo</p>
            </div>

            {/* Top buscas */}
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Top buscas (Search Console)</h2>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Busca</th><th>Cliques</th><th>Impressões</th><th>CTR</th><th>Posição</th><th></th>
                </tr>
              </thead>
              <tbody>
                {data.top_queries.map((q, i) => (
                  <tr key={i}>
                    <td className={styles.q}>{q.query}</td>
                    <td className={styles.num}>{fmt(q.clicks)}</td>
                    <td className={styles.num}>{fmt(q.impressions)}</td>
                    <td className={styles.num}>{(q.ctr * 100).toFixed(1)}%</td>
                    <td className={styles.num}>{q.position.toFixed(1)}</td>
                    <td><EditArticleButton id={q.wp_post_id} site={site} size="sm" variant="ghost" /></td>
                  </tr>
                ))}
                {data.top_queries.length === 0 && (
                  <tr><td colSpan={6} className={styles.muted}>Sem queries com dado ainda.</td></tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
            </>
          )}
        </>
      )}

      {tab === 'seo' && <Otimizacao site={site} embedded />}

      {tab === 'autores' && <AutoresSection site={site} />}

      {tab === 'custo' && <CustoSection />}
    </section>
  );
}

const _autorNome = (a: string) =>
  a === 'content-engine' ? 'Automático / não atribuído' : a.split('@')[0];

function CustoSection() {
  const { chartTheme, chartTooltipStyle } = useChartTheme();
  const costsQ = useQuery({
    queryKey: ['config', 'budgets', 'costs'],
    queryFn: () => budgetsApi.costs(31),
    staleTime: 60_000,
    retry: false,
  });
  const budgetsQ = useQuery({
    queryKey: ['config', 'budgets'],
    queryFn: () => budgetsApi.list(),
    staleTime: 60_000,
    retry: false,
  });
  const rep = costsQ.data;
  const usd = (v: number) => `$${v >= 1 ? v.toFixed(2) : v.toFixed(4)}`;
  const dias = (rep?.days ?? []).map((d) => ({ ...d, dia: d.day.slice(5) }));
  const mes = (budgetsQ.data ?? []).find((b) => b.provider === 'openai');
  const budgetNum = mes ? parseFloat(mes.budget_usd) : 0;

  if (costsQ.isLoading) return <p className={styles.muted}>Carregando…</p>;
  if (!rep || (rep.days.length === 0 && !mes)) {
    return (
      <p className={styles.empty}>
        Sem custo registrado ainda — o metering grava a partir da primeira
        geração. Defina o orçamento mensal em <b>Config → Custos</b>.
      </p>
    );
  }
  return (
    <>
      <div className={styles.kpis}>
        <Kpi group="Matérias" label={`custo ${rep.window_days}d`} value={usd(rep.total_usd)} big />
        <Kpi group="Mês (tudo)" label="gasto real (usage_budget)" value={mes ? usd(parseFloat(mes.spent_usd)) : '—'} />
        <Kpi group="Mês" label="orçamento" value={budgetNum > 0 ? usd(budgetNum) : 'não definido'} />
        <Kpi group="Mês" label="% consumido" value={budgetNum > 0 && mes ? `${Math.round(mes.spent_pct)}%` : '—'} />
      </div>

      {dias.length > 0 && (
        <div className={styles.cardWide}>
          <h2 className={styles.cardTitle}>Custo por dia (US$) — matérias geradas</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dias} margin={{ left: 8, right: 8, top: 8 }}>
              <CartesianGrid stroke={chartTheme.grid} vertical={false} />
              <XAxis dataKey="dia" stroke={chartTheme.axis} tick={{ fontSize: 10, fill: chartTheme.textMuted }} minTickGap={20} />
              <YAxis stroke={chartTheme.axis} tick={{ fontSize: 10, fill: chartTheme.textMuted }} />
              <Tooltip contentStyle={chartTooltipStyle} cursor={{ fill: chartTheme.brandBg }} />
              <Bar dataKey="cost_usd" name="US$" fill={chartTheme.brand} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className={styles.grid}>
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Custo por fonte</h2>
          <table className={styles.table}>
            <thead>
              <tr><th>Fonte</th><th>Matérias</th><th>US$</th></tr>
            </thead>
            <tbody>
              {rep.por_fonte.map((f, i) => (
                <tr key={i}>
                  <td className={styles.q}>{f.fonte}</td>
                  <td className={styles.num}>{f.materias}</td>
                  <td className={styles.num}>{usd(f.cost_usd)}</td>
                </tr>
              ))}
              {rep.por_fonte.length === 0 && (
                <tr><td colSpan={3} className={styles.muted}>Nada no período.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Custo por redator</h2>
          <table className={styles.table}>
            <thead>
              <tr><th>Redator</th><th>Matérias</th><th>US$</th></tr>
            </thead>
            <tbody>
              {rep.por_redator.map((r, i) => (
                <tr key={i}>
                  <td className={styles.q}>{_autorNome(r.redator)}</td>
                  <td className={styles.num}>{r.materias}</td>
                  <td className={styles.num}>{usd(r.cost_usd)}</td>
                </tr>
              ))}
              {rep.por_redator.length === 0 && (
                <tr><td colSpan={3} className={styles.muted}>Nada no período.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function AutoresSection({ site }: { site: string }) {
  const { chartTheme } = useChartTheme();
  const [aberto, setAberto] = useState<string | null>(null);
  const rankQ = useQuery({
    queryKey: ['metrics', 'ranking-autores', site],
    queryFn: () => metricsApi.rankingAutores({ site }),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const rows: RankingAutor[] = rankQ.data ?? [];
  const maxPv = Math.max(1, ...rows.map((r) => r.pageviews));

  if (rankQ.isLoading) return <p className={styles.muted}>Carregando ranking…</p>;
  if (rankQ.isError) return <p className={styles.muted}>Falha ao carregar o ranking de autores.</p>;
  if (!rows.length) return <p className={styles.muted}>Ainda sem dados de autoria com métricas.</p>;

  return (
    <div className={styles.card}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Autor</th>
            <th>Matérias</th>
            <th>Pageviews</th>
            <th>Cliques</th>
            <th>CTR</th>
            <th>Posição</th>
            <th>Leitura</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Fragment key={r.autor}>
              <tr
                onClick={() => setAberto(aberto === r.autor ? null : r.autor)}
                style={{ cursor: 'pointer' }}
                title="Clique para ver os artigos deste autor"
              >
                <td style={{ textAlign: 'left' }}>
                  <span style={{ marginRight: 6, opacity: 0.6 }}>{aberto === r.autor ? '▾' : '▸'}</span>
                  {_autorNome(r.autor)}
                </td>
                <td>{r.artigos}</td>
                <td>
                  <div style={{ position: 'relative', minWidth: 84 }}>
                    <span
                      style={{
                        position: 'absolute', left: 0, top: 2, bottom: 2,
                        width: `${(r.pageviews / maxPv) * 100}%`,
                        background: chartTheme.brand, opacity: 0.16, borderRadius: 4,
                      }}
                    />
                    <span style={{ position: 'relative' }}>{fmt(r.pageviews)}</span>
                  </div>
                </td>
                <td>{fmt(r.cliques)}</td>
                <td>{(r.ctr * 100).toFixed(1)}%</td>
                <td>{r.pos_media != null ? r.pos_media.toFixed(1) : '—'}</td>
                <td>{secs(r.tempo_medio)}</td>
              </tr>
              {aberto === r.autor && (
                <tr>
                  <td colSpan={7} style={{ padding: 0 }}>
                    <AutorArtigos autor={r.autor} site={site} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AutorArtigos({ autor, site }: { autor: string; site: string }) {
  const q = useQuery({
    queryKey: ['metrics', 'autor-artigos', autor, site],
    queryFn: () => metricsApi.autorArtigos(autor, { site, limit: 50 }),
    staleTime: 5 * 60_000,
    retry: false,
  });
  if (q.isLoading) return <p className={styles.muted} style={{ padding: 12 }}>Carregando artigos…</p>;
  const arts: AutorArtigo[] = q.data ?? [];
  if (!arts.length) return <p className={styles.muted} style={{ padding: 12 }}>Sem artigos com métricas.</p>;
  return (
    <table className={styles.table} style={{ margin: 0, opacity: 0.92 }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left' }}>Matéria</th>
          <th>Pageviews</th>
          <th>Cliques</th>
          <th>CTR</th>
          <th>Posição</th>
          <th>Leitura</th>
        </tr>
      </thead>
      <tbody>
        {arts.map((a) => (
          <tr key={a.wp_post_id}>
            <td
              style={{ textAlign: 'left', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={a.title || `#${a.wp_post_id}`}
            >
              {a.url ? (
                <a href={a.url} target="_blank" rel="noopener noreferrer">{a.title || `#${a.wp_post_id}`}</a>
              ) : (
                a.title || `#${a.wp_post_id}`
              )}
            </td>
            <td>{fmt(a.pageviews)}</td>
            <td>{fmt(a.gsc_clicks)}</td>
            <td>{(a.gsc_ctr * 100).toFixed(1)}%</td>
            <td>{a.gsc_position != null ? a.gsc_position.toFixed(1) : '—'}</td>
            <td>{secs(a.avg_engagement_time)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Kpi({ group, label, value, big }: { group: string; label: string; value: string; big?: boolean }) {
  return (
    <div className={styles.kpi}>
      <span className={styles.kpiGroup}>{group}</span>
      <span className={big ? styles.kpiBig : styles.kpiVal}>{value}</span>
      <span className={styles.kpiLabel}>{label}</span>
    </div>
  );
}

const SIGNAL_INFO: Record<string, { label: string; problem: string; action: string }> = {
  striking_distance: {
    label: 'Striking',
    problem: 'Striking distance — rankeia na posição 5–20 com muitas impressões. Está quase na 1ª página.',
    action: 'Otimize o título mirando a busca-alvo (expressão no começo) e reforce no 1º parágrafo + um <h2>.',
  },
  ctr_gap: {
    label: 'CTR baixo',
    problem: 'CTR gap — rankeia bem mas pouca gente clica. Título/resumo fracos no Google.',
    action: 'Reescreva título + meta description pra dar vontade de clicar (sem clickbait).',
  },
  cwv: {
    label: 'CWV',
    problem: 'Core Web Vitals ruim (LCP/CLS/INP) — página lenta ou instável. Afeta leitor e ranking.',
    action: 'Corrija imagem sem dimensão (mata o CLS) e lazy-load fora da dobra.',
  },
  quality_bounce: {
    label: 'Bounce',
    problem: 'Bounce de qualidade — muita gente entra e sai rápido (engajamento baixo).',
    action: 'Melhore o lead: responda a dúvida nos 2 primeiros parágrafos e corte enrolação.',
  },
  content_gap: {
    label: 'Gap',
    problem: 'Content gap — busca com impressão sem matéria dedicada.',
    action: 'Crie uma pauta mirando exatamente essa busca.',
  },
  decay: {
    label: 'Queda',
    problem: 'Decay — a matéria está perdendo posição/cliques com o tempo.',
    action: 'Atualize com fatos novos + data de atualização e republique.',
  },
};
const sigLabel = (t: string) => SIGNAL_INFO[t]?.label ?? t;
const sigProblem = (t: string) => SIGNAL_INFO[t]?.problem ?? t;
const sevTone = (s: string): 'crit' | 'warn' | 'neutral' =>
  s === 'high' ? 'crit' : s === 'medium' ? 'warn' : 'neutral';
const LEVEL_DOT: Record<string, string> = {
  critico: 'var(--sig-crit)',
  atencao: 'var(--sig-warn)',
  ok: 'var(--sig-ok)',
};

/** Manual prático (regras, instantâneo) a partir dos sinais do artigo. */
function buildManual(r: RundownRow): { problem: string; action: string }[] {
  const seen = new Set<string>();
  const out: { problem: string; action: string }[] = [];
  for (const s of r.signals) {
    if (seen.has(s.signal_type)) continue;
    seen.add(s.signal_type);
    const info = SIGNAL_INFO[s.signal_type];
    if (info) {
      out.push({
        problem: info.problem,
        action: info.action + (s.ref_key ? ` (busca: “${s.ref_key}”)` : ''),
      });
    }
  }
  return out;
}

type SortKey = 'pageviews' | 'gsc_position' | 'gsc_impressions' | 'gsc_clicks' | 'gsc_ctr' | 'avg_engagement_time';

function RundownSection({ site }: { site: string }) {
  const [editoria, setEditoria] = useState('');
  const [metodo, setMetodo] = useState('');
  const [fonte, setFonte] = useState('');
  const [level, setLevel] = useState('');
  const [origem, setOrigem] = useState('');
  const [janela, setJanela] = useState('28d');
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('pageviews');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [openRow, setOpenRow] = useState<RundownRow | null>(null);

  const facetsQ = useQuery({
    queryKey: ['metrics', 'facets', site],
    queryFn: () => metricsApi.facets(site),
    staleTime: 300_000,
  });
  const rundownQ = useQuery({
    queryKey: ['metrics', 'rundown', site, { editoria, metodo, fonte, level, origem, janela, q }],
    queryFn: () =>
      metricsApi.rundown(site, {
        editoria: editoria || undefined,
        metodo: metodo || undefined,
        fonte: fonte || undefined,
        level: level || undefined,
        origem: origem || undefined,
        window: janela,
        q: q || undefined,
      }),
    staleTime: 60_000,
  });
  const f = facetsQ.data ?? { editorias: [], metodos: [], fontes: [] };
  const rows = useMemo(() => {
    const list = [...(rundownQ.data ?? [])];
    const dir = sortDir === 'asc' ? 1 : -1;
    const fallback = sortKey === 'gsc_position' ? 999 : 0;
    list.sort((a, b) =>
      ((Number(a[sortKey] ?? fallback)) - (Number(b[sortKey] ?? fallback))) * dir);
    return list;
  }, [rundownQ.data, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir(k === 'gsc_position' ? 'asc' : 'desc'); // posição: melhor = menor
    }
  };
  const arrow = (k: SortKey) => (sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  return (
    <div className={styles.cardWide}>
      <div className={styles.rundownHead}>
        <h2 className={styles.cardTitle}>Rundown — suas matérias</h2>
        <span className={styles.muted}>{rows.length} matérias · {janela === '7d' ? '7 dias' : janela === '90d' ? '90 dias' : '28 dias'}</span>
      </div>
      <div className={styles.filters}>
        <input
          className={styles.filterInput}
          placeholder="buscar título…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select className={styles.filterSel} value={editoria} onChange={(e) => setEditoria(e.target.value)}>
          <option value="">Editoria: todas</option>
          {f.editorias.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select className={styles.filterSel} value={metodo} onChange={(e) => setMetodo(e.target.value)}>
          <option value="">Método: todos</option>
          {f.metodos.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select className={styles.filterSel} value={fonte} onChange={(e) => setFonte(e.target.value)}>
          <option value="">Fonte: todas</option>
          {f.fontes.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select className={styles.filterSel} value={level} onChange={(e) => setLevel(e.target.value)}>
          <option value="">Nível: todos</option>
          <option value="critico">Crítico</option>
          <option value="atencao">Atenção</option>
          <option value="ok">OK</option>
        </select>
        <select className={styles.filterSel} value={origem} onChange={(e) => setOrigem(e.target.value)}>
          <option value="">Origem: todas</option>
          {/* value é contrato de API (origem produzida pelo sistema); o rótulo
              não pode carregar marca de cliente no SaaS multi-tenant. */}
          <option value="gnc_desk">Do sistema</option>
          <option value="externo">Externo</option>
        </select>
        <select className={styles.filterSel} value={janela} onChange={(e) => setJanela(e.target.value)} title="Janela de tempo (funil computado da série diária)">
          <option value="7d">7 dias</option>
          <option value="28d">28 dias</option>
          <option value="90d">90 dias</option>
        </select>
      </div>
      <div className={styles.rdScroll}>
        <table className={styles.rdTable}>
          <thead>
            <tr>
              <th></th><th>Matéria</th>
              <th className={`${styles.num} ${styles.thSort}`} onClick={() => toggleSort('pageviews')}>PV{arrow('pageviews')}</th>
              <th className={`${styles.num} ${styles.thSort}`} onClick={() => toggleSort('gsc_position')}>Posição{arrow('gsc_position')}</th>
              <th className={`${styles.num} ${styles.thSort}`} onClick={() => toggleSort('gsc_impressions')}>Impressões{arrow('gsc_impressions')}</th>
              <th className={`${styles.num} ${styles.thSort}`} onClick={() => toggleSort('gsc_clicks')}>Cliques{arrow('gsc_clicks')}</th>
              <th className={`${styles.num} ${styles.thSort}`} onClick={() => toggleSort('gsc_ctr')}>CTR{arrow('gsc_ctr')}</th>
              <th className={`${styles.num} ${styles.thSort}`} onClick={() => toggleSort('avg_engagement_time')}>Tempo{arrow('avg_engagement_time')}</th>
              <th>Alertas / otimização</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <RundownRowView key={r.wp_post_id} r={r} onOpen={() => setOpenRow(r)} />
            ))}
            {rows.length === 0 && !rundownQ.isLoading && (
              <tr><td colSpan={9} className={styles.muted}>Nenhuma matéria com esse filtro.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {openRow && <OptimizeModal r={openRow} site={site} onClose={() => setOpenRow(null)} />}
    </div>
  );
}

function RundownRowView({ r, onOpen }: { r: RundownRow; onOpen: () => void }) {
  const manual = buildManual(r);
  return (
    <tr className={styles.rdRow} onClick={onOpen}>
      <td className={styles.rdThumbCell}>
        {r.thumbnail
          ? <img className={styles.rdThumb} src={r.thumbnail} alt="" loading="lazy" />
          : <div className={styles.rdThumbPh} />}
      </td>
      <td className={styles.rdTitleCell}>
        <div className={styles.rdTitleRow}>
          <span className={styles.rdLevel} style={{ background: LEVEL_DOT[r.level] }} title={r.level} />
          <div className={styles.rdTitleBox}>
            <span className={styles.rdName}>{r.title || `#${r.wp_post_id}`}</span>
            <span className={styles.rdMeta}>
              {r.editoria && <span className={styles.rdChip}>{r.editoria}</span>}
              {r.metodo && <span className={styles.rdChip}>{r.metodo}</span>}
              {r.fonte && <span className={styles.rdChip}>{r.fonte}</span>}
            </span>
          </div>
        </div>
        {manual.length > 0 && (
          <div className={styles.rowTip}>
            <span className={styles.rowTipHead}>O que fazer nesta matéria</span>
            {manual.map((m, i) => <div key={i} className={styles.rowTipItem}>• {m.action}</div>)}
            <span className={styles.rowTipFoot}>clique pra diagnosticar/corrigir com IA</span>
          </div>
        )}
      </td>
      <td className={styles.num}>{fmt(r.pageviews)}</td>
      <td className={styles.num}>{r.gsc_position != null ? r.gsc_position.toFixed(1) : '—'}</td>
      <td className={styles.num}>{fmt(r.gsc_impressions)}</td>
      <td className={styles.num}>{fmt(r.gsc_clicks)}</td>
      <td className={styles.num} title={r.gsc_impressions ? undefined : 'Sem impressões na busca (tráfego não veio do Google)'}>
        {r.gsc_impressions ? `${(r.gsc_ctr * 100).toFixed(1)}%` : '—'}
      </td>
      <td className={styles.num}>{secs(r.avg_engagement_time)}</td>
      <td>
        <div className={styles.rdSignals}>
          {r.signals.length === 0 && <span className={styles.muted}>—</span>}
          {r.signals.map((s, i) => (
            <span key={i} title={sigProblem(s.signal_type)}>
              <Badge tone={sevTone(s.severity)} size="xs">
                {sigLabel(s.signal_type)}{s.ref_key ? `: ${s.ref_key.slice(0, 16)}` : ''}
              </Badge>
            </span>
          ))}
        </div>
      </td>
    </tr>
  );
}

function OptimizeModal({ r, site, onClose }: { r: RundownRow; site: string; onClose: () => void }) {
  const qc = useQueryClient();
  const manual = buildManual(r);
  const topSig = r.signals.find(
    (s) => s.signal_type === 'striking_distance' || s.signal_type === 'ctr_gap',
  );
  const [variations, setVariations] = useState<{ title: string; meta: string }[] | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['metrics', 'rundown'] });
    qc.invalidateQueries({ queryKey: ['signals'] });
  };
  const suggest = useMutation({
    mutationFn: () => optimizationApi.suggest(topSig!.id),
    onSuccess: (res) => setVariations(res.variations),
  });
  const apply = useMutation({
    mutationFn: (v: { title: string; meta: string }) => optimizationApi.apply(topSig!.id, v),
    onSuccess: () => { invalidate(); onClose(); },
  });
  const fixDirect = useMutation({
    mutationFn: async () => {
      const res = variations ? { variations } : await optimizationApi.suggest(topSig!.id);
      const best = res.variations?.[0];
      if (!best) throw new Error('sem sugestão');
      return optimizationApi.apply(topSig!.id, best);
    },
    onSuccess: () => { invalidate(); onClose(); },
  });

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.optModal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.optHead}>
          <div>
            <h2 className={styles.optTitle}>{r.title || `#${r.wp_post_id}`}</h2>
            <span className={styles.muted}>
              pos {r.gsc_position != null ? r.gsc_position.toFixed(1) : '—'} · {fmt(r.gsc_impressions)} impr · {fmt(r.gsc_clicks)} cliques · CTR {(r.gsc_ctr * 100).toFixed(1)}%
            </span>
          </div>
          <button className={styles.x} onClick={onClose} aria-label="Fechar">×</button>
        </header>

        <div className={styles.diag}>
          <span className={styles.diagHead}>Diagnóstico — o que fazer</span>
          {manual.length === 0 && <p className={styles.muted}>Sem alertas. Matéria saudável. </p>}
          {manual.map((m, i) => (
            <div key={i} className={styles.diagItem}>
              <p className={styles.diagProblem}>{m.problem}</p>
              <p className={styles.diagAction}>{m.action}</p>
            </div>
          ))}
        </div>

        {topSig ? (
          <>
            <div className={styles.optActions}>
              <Button variant="secondary" loading={suggest.isPending} onClick={() => suggest.mutate()}>
                Gerar correções (IA)
              </Button>
              <Button
                loading={fixDirect.isPending}
                onClick={() => {
                  if (window.confirm('Corrigir automaticamente? A IA vai gerar e aplicar o melhor título + meta no WP agora.'))
                    fixDirect.mutate();
                }}
                title="Gera e aplica a melhor variação direto no WP"
              >
                Corrigir direto
              </Button>
            </div>
            {variations?.map((v, i) => (
              <div key={i} className={styles.variation}>
                <p className={styles.varTitle}>{v.title} <small>({v.title.length}c)</small></p>
                <p className={styles.varMeta}>{v.meta}</p>
                <div className={styles.varApply}>
                  <Button
                    size="sm"
                    loading={apply.isPending && apply.variables?.title === v.title}
                    onClick={() => { if (window.confirm(`Aplicar no WP?\n\n${v.title}`)) apply.mutate(v); }}
                  >
                    Aplicar esta
                  </Button>
                </div>
              </div>
            ))}
          </>
        ) : (
          <p className={styles.muted}>
            Os alertas desta matéria são técnicos/de engajamento (não de título/meta). Abra o editor para ajustar.
          </p>
        )}

        <div className={styles.optFoot}>
          <EditArticleButton id={r.wp_post_id} site={site} label="Abrir editor" />
        </div>
      </div>
    </div>
  );
}
